import { strIsNullOrEmpty } from '@fox/atoms/utils/string';
import { IBlockDefinition, IBlockProps } from '@fox/blocks/contracts/block';
import styled from 'styled-components';
import { QUESTION_BLOCK_TYPE } from '../../../form-block-types';
import { IQuestionAnswer } from '../../form-root/contracts';
import { FormRenderMode } from '../../form-runtime-context/contracts';
import { useFormRuntimeContext } from '../../form-runtime-context/form-runtime-context';
import { useFormBlockReRenderer } from '../../use-form-block-re-renderer';
import { useConditionEvaluator } from '../form-block/conditionals/helpers';
import { FormQuestionTypes, QuestionBlockMetadata, QuestionBlockProps } from './contracts';
import { QuestionDescriptionRender } from './question-description';
import { QuestionLabelRender } from './question-label';
import { RenderAddressQuestion } from './question-renders/render-address';
import { RenderChoiceQuestion } from './question-renders/render-choice';
import { RenderDataTableQuestion } from './question-renders/render-data-table';
import { RenderDateQuestion } from './question-renders/render-date';
import { RenderEmailQuestion } from './question-renders/render-email';
import { RenderMatrixScaleQuestion } from './question-renders/render-matrix-scale';
import { RenderNumberQuestion } from './question-renders/render-number';
import { RenderPhoneQuestion } from './question-renders/render-phone';
import { RenderTextQuestion } from './question-renders/render-text';

const SingleChoiceRatingWrapper = styled.div`
	display: grid;
	grid-template-columns: 1fr 4fr 1fr;
`;

const QuestionWrapper = styled.div`
	width: 100%;
	/* padding-right: 10px; */
	padding-top: 8.5px;
	min-height: 50px;

	gap: 10px;
	display: flex;
	flex-direction: column;
`;

/**
 * This wrapper hides blocks from being shown in the form but still allows them to render (as opposed to conditional preventing rendering). This means their hook effects such as filling in answers can still run.
 */
export const HideableQuestionWrapper = styled('div')<{ $hidden: boolean }>(({ $hidden }) => ({
	...($hidden && {
		position: 'absolute',
		width: 0,
		height: 0,
		overflow: 'hidden'
	})
}));

export function prefillStringAnswer(query: URLSearchParams, question: any): IQuestionAnswer | undefined {
	const answerString = query.get(question.name);
	if (!answerString) return undefined;

	return {
		questionName: question.name,
		answer: {
			answerString
		},
		type: question.type
	};
}

const RenderQuestion = (props: { id: string; type: FormQuestionTypes; props: QuestionBlockProps }) => {
	const { id, type, props: fieldProps } = props;

	const fieldMeta: QuestionBlockMetadata = fieldProps.metadataJson ? JSON.parse(fieldProps.metadataJson) : {};

	switch (type) {
		case FormQuestionTypes.Date:
			return <RenderDateQuestion fieldProps={fieldProps} questionName={fieldProps.name} inEditMode={false} id={id} />;
		case FormQuestionTypes.Email:
			return <RenderEmailQuestion questionName={fieldProps.name} inEditMode={false} id={id} />;
		case FormQuestionTypes.Phone:
			return <RenderPhoneQuestion questionName={fieldProps.name} inEditMode={false} id={id} mobile={false} />;
		case FormQuestionTypes.Mobile:
			return <RenderPhoneQuestion questionName={fieldProps.name} inEditMode={false} id={id} mobile={true} />;
		case FormQuestionTypes.Number:
			return <RenderNumberQuestion questionName={fieldProps.name} inEditMode={false} id={id} maxValue={fieldMeta.maxValue} />;
		case FormQuestionTypes.Address:
			return <RenderAddressQuestion questionName={fieldProps.name} id={id} />;
		case FormQuestionTypes.DataTable:
			return <RenderDataTableQuestion fieldProps={fieldProps} id={id} />;
		case FormQuestionTypes.MatrixScaleChoice:
			return (
				<RenderMatrixScaleQuestion
					options={fieldMeta.choices ?? []}
					questionName={fieldProps.name}
					questions={fieldMeta.questions ?? []}
					id={id}
					displayType={fieldMeta.displayType}
				/>
			);
		case FormQuestionTypes.SingleChoiceRating:
			return (
				<SingleChoiceRatingWrapper>
					<QuestionDescriptionRender id={props.id} style={{ textAlign: 'right' }} value={fieldMeta.ratingDescriptionLeft ?? ''} />

					<RenderChoiceQuestion
						id={props.id}
						fieldProps={fieldProps}
						options={fieldMeta.choices ?? []}
						allowMulti={false}
						allowOther={false}
						isRating={true}
						displayType={fieldMeta.displayType}
					/>

					<QuestionDescriptionRender id={props.id} value={fieldMeta.ratingDescriptionRight ?? ''} />
				</SingleChoiceRatingWrapper>
			);
		case FormQuestionTypes.SingleChoice:
			return (
				<RenderChoiceQuestion
					id={props.id}
					fieldProps={fieldProps}
					options={fieldMeta.choices ?? []}
					allowMulti={false}
					allowOther={false}
					isRating={false}
					displayType={fieldMeta.displayType}
					orientation={fieldMeta.orientation}
				/>
			);
		case FormQuestionTypes.Text:
			return (
				<RenderTextQuestion
					questionName={fieldProps.name}
					inEditMode={false}
					id={props.id}
					displayType={fieldMeta.displayType}
					maxLength={fieldMeta.maxLength}
				/>
			);
		case FormQuestionTypes.SingleChoiceWithOther:
			return (
				<RenderChoiceQuestion
					id={props.id}
					fieldProps={fieldProps}
					options={fieldMeta.choices ?? []}
					allowMulti={false}
					allowOther={true}
					isRating={false}
					displayType={fieldMeta.displayType}
					orientation={fieldMeta.orientation}
				/>
			);
		case FormQuestionTypes.MultiChoice:
			return (
				<RenderChoiceQuestion
					id={props.id}
					fieldProps={fieldProps}
					options={fieldMeta.choices ?? []}
					allowMulti={true}
					allowOther={false}
					isRating={false}
					displayType={fieldMeta.displayType}
					orientation={fieldMeta.orientation}
					selectAll={fieldMeta.selectAll}
				/>
			);
		case FormQuestionTypes.MultiChoiceWithOther:
			return (
				<RenderChoiceQuestion
					id={props.id}
					fieldProps={fieldProps}
					options={fieldMeta.choices ?? []}
					allowMulti={true}
					allowOther={true}
					isRating={false}
					displayType={fieldMeta.displayType}
					orientation={fieldMeta.orientation}
					selectAll={fieldMeta.selectAll}
				/>
			);
		default:
			return <>Please select a question type</>;
	}
};

export const QuestionBlock = ({ id, props }: IBlockProps<QuestionBlockProps>) => {
	const fieldMeta: QuestionBlockMetadata = props.metadataJson ? JSON.parse(props.metadataJson) : {};
	const { formRenderMode } = useFormRuntimeContext();
	const conditionsValid = useConditionEvaluator(props);
	useFormBlockReRenderer([props.name], props);
	// If there is a condition check it matches the required value before rendering the question
	if (!conditionsValid) {
		return null;
	}

	return (
		<HideableQuestionWrapper $hidden={!!fieldMeta.hidden && formRenderMode !== FormRenderMode.design}>
			<QuestionWrapper style={{ pointerEvents: formRenderMode === FormRenderMode.design ? 'none' : 'auto' }}>
				{!strIsNullOrEmpty(props.title) ? <QuestionLabelRender id={id} required={fieldMeta?.required} value={props.title} /> : <></>}
				{!strIsNullOrEmpty(props.description) ? <QuestionDescriptionRender id={id} value={props.description} /> : <></>}
				<RenderQuestion type={props.type} id={id} props={props} />
			</QuestionWrapper>
		</HideableQuestionWrapper>
	);
};

export const QuestionBlockRuntime: IBlockDefinition<QuestionBlockProps> = {
	Component: QuestionBlock,
	type: QUESTION_BLOCK_TYPE
};
